import React from 'react';
import PropTypes from 'prop-types';

/**
 * Badge component for labels, tags, and status indicators
 * Supports multiple variants and sizes with automatic color mapping for common statuses
 */
const Badge = ({
  children,
  variant = 'default',
  size = 'md',
  className = '',
}) => {
  const sizeClasses = {
    sm: 'tw-px-2 tw-py-0.5 tw-text-xs',
    md: 'tw-px-2.5 tw-py-1 tw-text-sm',
    lg: 'tw-px-3 tw-py-1.5 tw-text-base',
  };

  const variantClasses = {
    default:     'tw-bg-slate-100 tw-text-slate-800',
    primary:     'tw-bg-blue-100 tw-text-blue-900',
    success:     'tw-bg-green-100 tw-text-green-900',
    warning:     'tw-bg-yellow-100 tw-text-yellow-900',
    danger:      'tw-bg-red-100 tw-text-red-900',
    info:        'tw-bg-cyan-100 tw-text-cyan-900',
    // Sales pipeline stages (Pebsteel)
    new_pcv:     'tw-bg-slate-100 tw-text-slate-700',
    quotation:   'tw-bg-blue-100 tw-text-blue-800',
    tendering:   'tw-bg-sky-100 tw-text-sky-800',
    negotiation: 'tw-bg-amber-100 tw-text-amber-800 tw-font-semibold',
    won:         'tw-bg-emerald-100 tw-text-emerald-800 tw-font-semibold',
    lost:        'tw-bg-red-100 tw-text-red-800 tw-font-semibold',
    cancelled:   'tw-bg-red-100 tw-text-red-800 tw-font-semibold',
    signed:      'tw-bg-emerald-100 tw-text-emerald-800 tw-font-semibold',
    execution:   'tw-bg-purple-100 tw-text-purple-800 tw-font-semibold',
    completed:   'tw-bg-emerald-100 tw-text-emerald-800 tw-font-semibold',
    pending:     'tw-bg-amber-100 tw-text-amber-800',
    draft:       'tw-bg-slate-100 tw-text-slate-700',
  };

  const finalClassName = `${sizeClasses[size]} ${variantClasses[variant] || variantClasses.default} tw-inline-flex tw-items-center tw-gap-1.5 tw-font-medium tw-rounded-xl tw-whitespace-nowrap tw-transition-colors ${className}`;

  return <span className={finalClassName}>{children}</span>;
};

/**
 * Auto-detect badge variant based on status text
 */
Badge.getVariantFromStatus = (status) => {
  if (!status) return 'default';
  const lower = status.toLowerCase();

  if (lower.includes('lost') || lower.includes('cancel'))               return 'lost';
  if (lower === 'won' || lower.includes('sign') || lower.includes('complet')) return 'won';
  if (lower.includes('negot'))                                          return 'negotiation';
  if (lower.includes('tender'))                                         return 'tendering';
  if (lower.includes('quotation') || lower.includes('quot'))            return 'quotation';
  if (lower.includes('new pcv') || lower.includes('new_pcv'))           return 'new_pcv';
  if (lower.includes('execut'))                                         return 'execution';
  if (lower.includes('pending') || lower.includes('draft'))             return 'pending';

  return 'default';
};

Badge.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf([
    'default', 'primary', 'success', 'warning', 'danger', 'info',
    'new_pcv', 'quotation', 'tendering', 'negotiation', 'won',
    'lost', 'cancelled', 'signed', 'execution', 'completed', 'pending', 'draft'
  ]),
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  className: PropTypes.string,
};

export default Badge;
